package pl.mothdigital.neuronowi.components.layouts

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.AlignItems
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.silk.components.graphics.*
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.keywords.*
import pl.mothdigital.neuronowi.common.model.ImageResource
import pl.mothdigital.neuronowi.common.utils.aspectRatio
import pl.mothdigital.neuronowi.common.utils.loading
import pl.mothdigital.neuronowi.theme.*

/**
 * Position of the image relative to content.
 */
enum class ImagePosition {
    LEFT,
    RIGHT
}

/**
 * Responsive layout component that displays an image alongside text content.
 *
 * Desktop layout: Image and content displayed side-by-side
 * Mobile layout: Content above, image below
 *
 * @param image The image resource to display
 * @param imagePosition Position of image (LEFT or RIGHT) on desktop
 * @param imageWidth Width of the image
 * @param imageBorderRadius Border radius for the image
 * @param gap Gap between image and content
 * @param breakpoint Breakpoint for switching between desktop/mobile layouts
 * @param modifier Optional modifier for the container
 * @param content Composable content to display alongside the image
 */
@Composable
fun ResponsiveImageTextLayout(
    image: ImageResource,
    imagePosition: ImagePosition = ImagePosition.LEFT,
    imageWidth: CSSSizeValue<CSSUnit.px> = 400.px,
    imageBorderRadius: CSSSizeValue<CSSUnit.rem> = 1.cssRem,
    gap: CSSSizeValue<CSSUnit.rem> = 3.cssRem,
    breakpoint: Breakpoint = Breakpoint.LG,
    modifier: Modifier = Modifier,
    content: @Composable ColumnScope.() -> Unit
) {
    // Desktop layout: side-by-side
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .flexWrap(FlexWrap.Wrap)
            .displayIfAtLeast(breakpoint)
            .gap(gap)
            .then(modifier),
    ) {
        if (imagePosition == ImagePosition.LEFT) {
            ImageBlock(image, imageWidth, imageBorderRadius)
        }

        Column(
            modifier = Modifier
                .flex(1)
                .gap(1.5.cssRem)
                .padding(1.cssRem),
            content = content
        )

        if (imagePosition == ImagePosition.RIGHT) {
            ImageBlock(image, imageWidth, imageBorderRadius)
        }
    }

    // Mobile layout: content above, image below
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .alignItems(AlignItems.Center)
            .displayUntil(breakpoint)
            .gap(1.5.cssRem)
            .padding(1.cssRem)
            .then(modifier),
    ) {
        content()

        ImageBlock(image, imageWidth, imageBorderRadius)
    }
}

@Composable
private fun ImageBlock(
    image: ImageResource,
    imageWidth: CSSSizeValue<CSSUnit.px>,
    imageBorderRadius: CSSSizeValue<CSSUnit.rem>
) {
    Image(
        src = image.src,
        alt = image.description,
        modifier = Modifier
            .width(imageWidth)
            .thenIf(image.aspectRatio != null, Modifier.aspectRatio(image.aspectRatio!!))
            .loading()
            .borderRadius(imageBorderRadius)
            .boxShadow(blurRadius = 8.px, color = SiteTheme.black._100)
    )
}
